/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { useCallback } from 'react';
import { NewsItem } from '../types';
import { Policy } from '../types/policy';
import { playUiSfx } from '../utils/uiAudio';
import {
  resolveCreatePolicy,
  resolveApprovePolicy,
  resolveSubmitPolicyForReview,
  resolveImplementPolicy,
  resolveActivatePolicy,
  resolveActivatePolicyChallenge,
  resolveTriggerPolicyAudit,
} from '../src/adapters/ui/hooks/usePolicyMutations';

export interface UsePolicyHandlersDeps {
  setPolicies: React.Dispatch<React.SetStateAction<Policy[]>>;
  setPolicyChallenges: React.Dispatch<React.SetStateAction<import('../types/policy').PolicyChallenge[]>>;
  addNewsItem: (item: NewsItem) => void;
}

export function usePolicyHandlers({
  setPolicies,
  setPolicyChallenges,
  addNewsItem,
}: UsePolicyHandlersDeps) {
  const handleCreatePolicy = useCallback((templateId: string, customization: Partial<Policy>) => {
    const result = resolveCreatePolicy({ templateId, customization });

    if (result.ok && result.policy) {
      setPolicies((prev) => [...prev, result.policy!]);
      addNewsItem({
        id: Date.now().toString(),
        text: `NEW POLICY DRAFT: ${result.policy.name} created for review`,
        type: 'neutral',
      });
      playUiSfx('click');
      return result.policy;
    }

    console.error('Failed to create policy:', result.error);
    addNewsItem({
      id: Date.now().toString(),
      text: `POLICY CREATION FAILED: ${result.error ?? 'Unknown error'}`,
      type: 'negative',
    });
    playUiSfx('error');
    return null;
  }, [addNewsItem, setPolicies]);

  const handleSubmitPolicyForReview = useCallback((policyId: string) => {
    const result = resolveSubmitPolicyForReview({ policyId });

    if (result.ok && result.policy) {
      setPolicies((prev) => prev.map((p) => (p.id === policyId ? result.policy! : p)));
      addNewsItem({
        id: Date.now().toString(),
        text: `POLICY SUBMITTED: ${result.policy.name} sent for review`,
        type: 'neutral',
      });
      playUiSfx('click');
      return;
    }

    console.error('Failed to submit policy for review:', result.error);
    addNewsItem({
      id: Date.now().toString(),
      text: `POLICY SUBMISSION FAILED: ${result.error ?? 'Unknown error'}`,
      type: 'negative',
    });
    playUiSfx('error');
  }, [addNewsItem, setPolicies]);

  const handleApprovePolicy = useCallback((policyId: string) => {
    const result = resolveApprovePolicy({ policyId, approver: 'Player' });

    if (result.ok && result.policy) {
      setPolicies((prev) => prev.map((p) => (p.id === policyId ? result.policy! : p)));
      addNewsItem({
        id: Date.now().toString(),
        text: `POLICY APPROVED: ${result.policy.name} cleared for implementation`,
        type: 'positive',
      });
      playUiSfx('success');
      return;
    }

    console.error('Failed to approve policy:', result.error);
    addNewsItem({
      id: Date.now().toString(),
      text: `POLICY APPROVAL FAILED: ${result.error ?? 'Unknown error'}`,
      type: 'negative',
    });
    playUiSfx('error');
  }, [addNewsItem, setPolicies]);

  const handleImplementPolicy = useCallback((policyId: string) => {
    const result = resolveImplementPolicy({ policyId, implementer: 'Player' });

    if (result.ok && result.policy) {
      setPolicies((prev) => prev.map((p) => (p.id === policyId ? result.policy! : p)));
      addNewsItem({
        id: Date.now().toString(),
        text: `POLICY IMPLEMENTATION: ${result.policy.name} deployment started`,
        type: 'positive',
      });
      playUiSfx('success');
      return;
    }

    console.error('Failed to implement policy:', result.error);
    addNewsItem({
      id: Date.now().toString(),
      text: `POLICY IMPLEMENTATION FAILED: ${result.error ?? 'Unknown error'}`,
      type: 'negative',
    });
    playUiSfx('error');
  }, [addNewsItem, setPolicies]);

  const handleEnforcePolicy = useCallback((policyId: string) => {
    const result = resolveActivatePolicy({ policyId });

    if (result.ok && result.policy) {
      setPolicies((prev) => prev.map((p) => (p.id === policyId ? result.policy! : p)));
      addNewsItem({
        id: Date.now().toString(),
        text: `POLICY ENFORCED: ${result.policy.name} is now active with compliance monitoring`,
        type: 'positive',
      });
      playUiSfx('success');
      return;
    }

    console.error('Failed to enforce policy:', result.error);
    addNewsItem({
      id: Date.now().toString(),
      text: `POLICY ENFORCEMENT FAILED: ${result.error ?? 'Unknown error'}`,
      type: 'negative',
    });
    playUiSfx('error');
  }, [addNewsItem, setPolicies]);

  const handleActivateChallenge = useCallback((challengeId: string) => {
    const result = resolveActivatePolicyChallenge({ challengeId });

    if (result.ok && result.challenges) {
      setPolicyChallenges(result.challenges);
      if (result.activatedChallenge) {
        addNewsItem({
          id: Date.now().toString(),
          text: `CHALLENGE ACTIVATED: ${result.activatedChallenge.name}`,
          type: 'neutral',
        });
        playUiSfx('click');
      }
      return;
    }

    console.error('Failed to activate challenge:', result.error);
    playUiSfx('error');
  }, [addNewsItem, setPolicyChallenges]);

  const handlePolicyAudit = useCallback((policyId?: string) => {
    const result = resolveTriggerPolicyAudit({ policyId });

    if (result.ok && result.audit) {
      addNewsItem({
        id: Date.now().toString(),
        text: `POLICY AUDIT COMPLETED: ${result.audit.summary.totalViolations} violations found`,
        type: result.audit.summary.criticalIssues > 0 ? 'negative' : 'positive',
      });
      playUiSfx(result.audit.summary.criticalIssues > 0 ? 'alert' : 'success');
      return;
    }

    console.error('Failed to run policy audit:', result.error);
    playUiSfx('error');
  }, [addNewsItem]);

  return {
    handleCreatePolicy,
    handleSubmitPolicyForReview,
    handleApprovePolicy,
    handleImplementPolicy,
    handleEnforcePolicy,
    handleActivateChallenge,
    handlePolicyAudit,
  };
}